import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  AutoFixHigh as GenerateIcon,
  GetApp as DownloadIcon,
  Email as EmailIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';

/**
 * Vehicle Inspection Detail View
 * Displays complete inspection details with all form sections
 */
const InspectionDetailView = ({ inspectionId, onBack }) => {
  const { t } = useTranslation();
  const [inspection, setInspection] = useState(null);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [pdfStatus, setPdfStatus] = useState({ checking: false, available: false });
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (inspectionId) {
      fetchInspection();
    }
  }, [inspectionId]);

  const fetchInspection = async () => {
    try {
      setLoading(true);
      const response = await makeApiCall(`api/inspections/${inspectionId}`);
      
      if (response.ok) {
        const data = await response.json();
        setInspection(data.inspection);
        setForms(data.forms || []);
        setPdfStatus({ checking: false, available: !!data.inspection?.has_pdf_report });
      } else {
        setError('Failed to load inspection');
      }
    } catch (error) {
      console.error('Error fetching inspection:', error);
      setError('Failed to load inspection');
    } finally {
      setLoading(false);
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const pollPdfJobUntilComplete = async (jobId) => {
    const maxAttempts = 120;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await sleep(2000);
      const statusResponse = await makeApiCall(`api/inspections/pdf-jobs/${jobId}`);
      if (!statusResponse.ok) {
        throw new Error(t('inspections.messages.failedToGeneratePdf'));
      }

      const statusData = await statusResponse.json();
      if (statusData.status === 'completed') {
        return statusData.result;
      }
      if (statusData.status === 'failed') {
        throw new Error(statusData.error || t('inspections.messages.failedToGeneratePdf'));
      }
    }
    throw new Error(t('inspections.messages.pdfGenerationTimedOut'));
  };

  const handleGeneratePdf = async () => {
    try {
      setGeneratingPdf(true);
      const response = await makeApiCall(`api/inspections/${inspectionId}/generate-pdf`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(t('inspections.messages.failedToGeneratePdf'));
      }

      const data = await response.json();
      if (!data.jobId) {
        throw new Error(t('inspections.messages.failedToGeneratePdf'));
      }

      await pollPdfJobUntilComplete(data.jobId);
      setInspection((prev) => (
        prev ? { ...prev, has_pdf_report: true } : prev
      ));
      setPdfStatus({ checking: false, available: true });
      alert(t('inspections.messages.pdfGenerated'));
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(error.message || t('inspections.messages.failedToGeneratePdf'));
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await makeApiCall(`api/inspections/${inspectionId}/pdf-download`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'PDF_NOT_GENERATED') {
          alert(t('inspections.messages.pdfRequired'));
          return;
        }
        throw new Error(t('inspections.messages.failedToDownloadPdf'));
      }

      const data = await response.json();

      const a = document.createElement('a');
      a.href = data.signed_url;
      a.download = data.filename || `inspection-${inspectionId}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(error.message || t('inspections.messages.failedToDownloadPdf'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleEmailInspection = async () => {
    setEmailDialog(true);
    setEmailData({ 
      email: '', 
      subject: t('inspections.messages.emailReportDetailSubject', { id: inspectionId, vehicleName: inspection?.vehicle_name }), 
      message: t('inspections.messages.emailReportDetailMessage', { vehicleName: inspection?.vehicle_name, plateNo: inspection?.plate_no })
    });
    setPdfStatus({ checking: true, available: false });
    try {
      const response = await makeApiCall(`api/inspections/${inspectionId}/pdf-status`);
      if (response.ok) {
        const data = await response.json();
        setPdfStatus({ checking: false, available: data.available });
      } else {
        setPdfStatus({ checking: false, available: false });
      }
    } catch {
      setPdfStatus({ checking: false, available: false });
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.email) {
      alert(t('inspections.messages.pleaseEnterEmail'));
      return;
    }

    try {
      setSendingEmail(true);
      const response = await makeApiCall(`api/inspections/${inspectionId}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        alert(t('inspections.messages.inspectionReportSent'));
        setEmailDialog(false);
        setEmailData({ email: '', subject: '', message: '' });
      } else {
        const errorData = await response.json();
        if (errorData.error === 'EMAIL_AUTH_FAILED') {
          alert(t('inspections.messages.emailAuthFailed'));
        } else {
          alert(errorData.error || t('inspections.messages.failedToSendEmail'));
        }
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert(t('inspections.messages.failedToSendEmail'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCloseEmailDialog = () => {
    setEmailDialog(false);
    setEmailData({ email: '', subject: '', message: '' });
  };

  const getDefectIcon = () => {
    if (inspection?.has_major_defect) return <CancelIcon color="error" />;
    if (inspection?.has_minor_defect) return <WarningIcon color="warning" />;
    return <CheckIcon color="success" />;
  };

  const getDefectChip = () => {
    if (inspection?.has_major_defect) {
      return <Chip label={t('inspections.statusLabels.outOfService')} color="error" />;
    }
    if (inspection?.has_minor_defect) {
      return <Chip label={t('inspections.statusLabels.minorDefectsFound')} color="warning" />;
    }
    if (inspection?.has_no_defects) {
      return <Chip label={t('inspections.statusLabels.noDefectsPass')} color="success" />;
    }
    return <Chip label={t('inspections.statusLabels.unknown')} />;
  };

  const getFormName = (formId) => {
    const formNames = {
      0: t('inspections.title'),
      1: t('inspections.sections.airBrakeSystem'),
      2: t('inspections.sections.cab'),
      3: t('inspections.sections.cargoSecurement'),
      4: t('inspections.sections.couplingDevices'),
      5: t('inspections.sections.dangerousGoods'),
      6: t('inspections.sections.driverControls'),
      7: t('inspections.sections.driverSeat'),
      8: t('inspections.sections.electricBrakeSystem'),
      9: t('inspections.sections.emergencyEquipment'),
      10: t('inspections.sections.exhaustSystem'),
      11: t('inspections.sections.frameAndCargoBody'),
      12: t('inspections.sections.fuelSystem'),
      13: t('inspections.sections.general'),
      14: t('inspections.sections.glassAndMirrors'),
      15: t('inspections.sections.heaterDefroster'),
      16: t('inspections.sections.horn'),
      17: t('inspections.sections.hydraulicBrakeSystem'),
      18: t('inspections.sections.lampsAndReflectors'),
      19: t('inspections.sections.steering'),
      20: t('inspections.sections.suspensionSystem'),
      21: t('inspections.sections.tires'),
      22: t('inspections.sections.wheelsHubsFasteners'),
      23: t('inspections.sections.windshieldWiperWasher'),
      24: t('inspections.reviewAndSubmit')
    };
    return formNames[formId] || t('inspections.statusLabels.unknown');
  };

  const getActionTypeLabel = (actionTypeId) => {
    const labels = {
      1: t('inspections.statusLabels.ok'),
      2: t('inspections.statusLabels.na'),
      3: t('inspections.statusLabels.majorDefect'),
      4: t('inspections.statusLabels.minorDefect'),
      5: t('inspections.statusLabels.bothMajorMinor')
    };
    return labels[actionTypeId] || t('inspections.statusLabels.unknown');
  };

  const getActionTypeColor = (actionTypeId) => {
    const colors = {
      1: 'success',
      2: 'default',
      3: 'error',
      4: 'warning',
      5: 'error'
    };
    return colors[actionTypeId] || 'default';
  };

  const parseFormData = (formData) => {
    if (!formData) return {};
    if (typeof formData === 'string') {
      try {
        return JSON.parse(formData);
      } catch {
        return {};
      }
    }
    return formData;
  };

  const inspectedForms = forms.filter((form) => form.form_id !== 24);
  const reviewData = parseFormData(forms.find((form) => form.form_id === 24)?.form_data);
  const signatureName = reviewData.employee_signed_name || inspection?.driver || inspection?.user_name || '';
  const signatureDate = reviewData.employee_sign_date || inspection?.updated_at || inspection?.created_at;
  const signatureImage = typeof reviewData.employee_signature === 'string' && reviewData.employee_signature.startsWith('data:image/')
    ? reviewData.employee_signature
    : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !inspection) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || t('inspections.messages.inspectionNotFound')}</Alert>
        <Button startIcon={<BackIcon />} onClick={onBack} sx={{ mt: 2 }}>
          {t('inspections.messages.backToList')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={onBack}
            variant="outlined"
          >
            {t('common.back')}
          </Button>
          <Typography variant="h4">
            {t('inspections.inspectionNumber', { id: inspection.id })}
          </Typography>
          {getDefectIcon()}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={generatingPdf ? <CircularProgress size={20} color="inherit" /> : <GenerateIcon />}
            onClick={handleGeneratePdf}
            disabled={generatingPdf}
            aria-label={t('inspections.tooltips.generatePdf')}
          >
            {generatingPdf ? t('inspections.messages.generatingPdf') : t('inspections.tooltips.generatePdf')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={handleEmailInspection}
            disabled={!inspection?.has_pdf_report}
            aria-label={t('inspections.tooltips.emailReport')}
          >
            {t('inspections.tooltips.emailReport')}
          </Button>
          <Button
            variant="contained"
            startIcon={downloadingPdf ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadPdf}
            disabled={downloadingPdf || !inspection?.has_pdf_report}
            aria-label={t('inspections.tooltips.downloadPdf')}
          >
            {t('inspections.tooltips.downloadPdf')}
          </Button>
        </Box>
      </Box>

      {/* Status Alert */}
      {inspection.has_major_defect && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t('inspections.alerts.vehicleOutOfService')}
          </Typography>
          <Typography variant="body2">
            {t('inspections.alerts.vehicleMustNotOperate')}
          </Typography>
        </Alert>
      )}

      {/* Main Information */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('inspections.vehicleInformation')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">{t('inspections.vehicle')}</Typography>
                  <Typography variant="body1" fontWeight="medium">{inspection.vehicle_name}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">{t('inspections.plateNumber')}</Typography>
                  <Typography variant="body1" fontWeight="medium">{inspection.plate_no || t('inspections.statusLabels.na')}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">{t('inspections.vehicleType')}</Typography>
                  <Typography variant="body1" fontWeight="medium">{inspection.vehicle_type}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">{t('inspections.tripType')}</Typography>
                  <Typography variant="body1" fontWeight="medium">{inspection.trip_type}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">{t('inspections.odometer')}</Typography>
                  <Typography variant="body1" fontWeight="medium">{inspection.odometer || t('inspections.statusLabels.na')}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">{t('inspections.location')}</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {inspection.is_custom_location_added ? inspection.custom_location : inspection.location}
                  </Typography>
                </Grid>
                {inspection.is_trailer_attached && (
                  <>
                    <Grid size={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        {t('inspections.trailerInformation')}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="textSecondary">{t('inspections.trailer')}</Typography>
                      <Typography variant="body1" fontWeight="medium">{inspection.trailer_name}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="textSecondary">{t('inspections.plateNumber')}</Typography>
                      <Typography variant="body1" fontWeight="medium">{inspection.trailer_plate_no || t('inspections.statusLabels.na')}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('inspections.inspectionDetails')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">{t('inspections.driver')}</Typography>
                  <Typography variant="body1" fontWeight="medium">{inspection.driver || inspection.user_name}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">{t('inspections.date')}</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {format(new Date(inspection.created_at), 'MMM dd, yyyy h:mm a')}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>{t('inspections.status')}</Typography>
                  {getDefectChip()}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Inspection Forms */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('inspections.inspectionForms')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {inspectedForms.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  {t('inspections.messages.noFormDataAvailable')}
                </Typography>
              ) : (
                <List>
                  {inspectedForms.map((form, index) => (
                    <React.Fragment key={form.id}>
                      <ListItem
                        sx={{
                          bgcolor: form.action_type_id !== 1 ? 'error.lighter' : 'transparent',
                          borderRadius: 1,
                          mb: 1
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle1" fontWeight="medium">
                                {getFormName(form.form_id)}
                              </Typography>
                              <Chip
                                label={getActionTypeLabel(form.action_type_id)}
                                color={getActionTypeColor(form.action_type_id)}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            form.form_data && (form.form_data.minorDefectNotes || form.form_data.majorDefectNotes) ? (
                              <Box sx={{ mt: 1 }}>
                                {form.form_data.minorDefectNotes && (
                                  <Typography variant="body2">
                                    <strong>{t('inspections.labels.notes')}:</strong> {form.form_data.minorDefectNotes}
                                  </Typography>
                                )}
                                {form.form_data.majorDefectNotes && (
                                  <Typography variant="body2" color="error">
                                    <strong>{t('inspections.labels.majorDefectNotes')}</strong> {form.form_data.majorDefectNotes}
                                  </Typography>
                                )}
                              </Box>
                            ) : null
                          }
                        />
                      </ListItem>
                      {index < inspectedForms.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('inspections.employeeSignature', 'Employee Signature')}
                </Typography>
                {signatureImage ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      bgcolor: '#f8fafc'
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, md: 5 }}>
                        <Typography variant="body2" color="textSecondary">
                          {t('inspections.driver', 'Driver')}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {signatureName || t('inspections.statusLabels.na')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          {t('inspections.date', 'Date')}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {signatureDate ? format(new Date(signatureDate), 'MMM dd, yyyy h:mm a') : t('inspections.statusLabels.na')}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 7 }}>
                        <Box
                          component="img"
                          src={signatureImage}
                          alt={t('inspections.employeeSignature', 'Employee signature')}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: 140,
                            p: 1,
                            bgcolor: 'white',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Alert severity="info">
                    {t('inspections.noSignatureCaptured', 'No employee signature was captured for this inspection.')}
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Email Dialog */}
      <Dialog open={emailDialog} onClose={handleCloseEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('inspections.emailInspectionReport')}</DialogTitle>
        <DialogContent>
          {pdfStatus.checking ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : !pdfStatus.available ? (
            <Alert severity="warning" sx={{ mt: 1 }} aria-label={t('inspections.messages.pdfRequiredAlert')}>
              {t('inspections.messages.pdfRequired')}
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('inspections.emailAddress')}
                  type="email"
                  value={emailData.email}
                  onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                  placeholder={t('inspections.emailPlaceholder')}
                  aria-label={t('inspections.emailAddress')}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('inspections.subject')}
                  value={emailData.subject}
                  onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  aria-label={t('inspections.subject')}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('inspections.message')}
                  value={emailData.message}
                  onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                  placeholder={t('inspections.messagePlaceholder')}
                  aria-label={t('inspections.message')}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={sendingEmail}>
            {t('common.cancel')}
          </Button>
          {pdfStatus.available && (
            <Button 
              onClick={handleSendEmail} 
              variant="contained" 
              disabled={sendingEmail || !emailData.email}
              startIcon={sendingEmail ? <CircularProgress size={20} /> : <EmailIcon />}
              aria-label={t('inspections.sendEmail')}
            >
              {sendingEmail ? t('inspections.sending') : t('inspections.sendEmail')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InspectionDetailView;
